import { osnovaApi } from '../base';
import type {
  LogistDictName,
  LogistContainerType,
  LogistEdoBox,
  LogistPostalAddress,
  LogistEtrn,
  LogistEtrnTitle,
  LogistEtrnEvidence,
  LogistTask,
  LogistDeliveryAcceptance,
  LogistEtrnInbox,
  LogistMatchCandidate,
  LogistOrder,
  LogistOrderExtract,
  LogistSigner,
  LogistSignerStatus,
  LogistTrip,
  LogistTripLoadingDetails,
  LogistTripSeries,
  LogistTripWeighing,
} from '@entities/logistics';

/**
 * ОСНОВА.Логист (ЭТрН, ТЗ AI-LOGIST-2026) — RTK Query эндпоинты.
 *
 * Справочники `/agents/logist/dictionaries/{dict}` где dict ∈ drivers | vehicles
 * | trailers. Тело — соответствующий New*-DTO (числа/строки как в форме); ответ
 * содержит { dict, item(s) }. Тип item уточняется на стороне компонента по dict.
 */
export const logistApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    /** Карточки подписантов титулов (§4.2). */
    listLogistSigners: b.query<{ items: LogistSigner[] }, { active?: boolean } | void>({
      query: (arg) => ({
        url: '/agents/logist/signers',
        params: arg && arg.active !== undefined ? { active: arg.active } : undefined,
      }),
      providesTags: ['LogistSigner'],
    }),

    /**
     * Классификатор оснований полномочий. Приходит с бэка, а не задан на
     * фронте: список определяет оператор ИС ЭПД, копиям расходиться нельзя.
     */
    listLogistSignerStatuses: b.query<{ items: LogistSignerStatus[] }, void>({
      query: () => ({ url: '/agents/logist/signers/statuses' }),
    }),

    /** Создать или обновить карточку (одна на сотрудника). */
    upsertLogistSigner: b.mutation<LogistSigner, Record<string, unknown>>({
      query: (body) => ({ url: '/agents/logist/signers', method: 'POST', body }),
      invalidatesTags: ['LogistSigner'],
    }),

    /** Включить/отключить карточку (без удаления: на неё ссылаются документы). */
    setLogistSignerActive: b.mutation<LogistSigner, { id: string; active: boolean }>({
      query: ({ id, active }) => ({
        url: `/agents/logist/signers/${id}/${active ? 'activate' : 'deactivate'}`,
        method: 'POST',
      }),
      invalidatesTags: ['LogistSigner'],
    }),

    /** Список записей справочника (?active — по умолчанию true на бэке). */
    listLogistDict: b.query<
      { dict: LogistDictName; items: unknown[] },
      { dict: LogistDictName; active?: boolean }
    >({
      query: ({ dict, active }) => ({
        url: `/agents/logist/dictionaries/${dict}`,
        params: active === undefined ? undefined : { active },
      }),
      providesTags: (_r, _e, { dict }) => [{ type: 'LogistDictionary', id: dict }],
    }),

    /** Создать запись справочника. */
    createLogistDict: b.mutation<
      { dict: LogistDictName; item: unknown },
      { dict: LogistDictName; body: Record<string, unknown> }
    >({
      query: ({ dict, body }) => ({
        url: `/agents/logist/dictionaries/${dict}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { dict }) => [{ type: 'LogistDictionary', id: dict }],
    }),

    /** Обновить запись справочника. */
    updateLogistDict: b.mutation<
      { dict: LogistDictName; item: unknown },
      { dict: LogistDictName; id: string; body: Record<string, unknown> }
    >({
      query: ({ dict, id, body }) => ({
        url: `/agents/logist/dictionaries/${dict}/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, { dict }) => [{ type: 'LogistDictionary', id: dict }],
    }),

    /** Активировать запись. */
    activateLogistDict: b.mutation<
      { dict: LogistDictName; id: string; is_active: boolean },
      { dict: LogistDictName; id: string }
    >({
      query: ({ dict, id }) => ({
        url: `/agents/logist/dictionaries/${dict}/${id}/activate`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, { dict }) => [{ type: 'LogistDictionary', id: dict }],
    }),

    /** Деактивировать (is_active=false; строка не удаляется). */
    deactivateLogistDict: b.mutation<
      { dict: LogistDictName; id: string; is_active: boolean },
      { dict: LogistDictName; id: string }
    >({
      query: ({ dict, id }) => ({
        url: `/agents/logist/dictionaries/${dict}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { dict }) => [{ type: 'LogistDictionary', id: dict }],
    }),

    // ---- Заказы на перевозку (§8.1) --------------------------------------

    /** Список заказов (опц. фильтр ?status, ?project_id). */
    listLogistOrders: b.query<
      { orders: LogistOrder[] },
      { status?: string; project_id?: string } | void
    >({
      query: (p) => ({
        url: '/agents/logist/orders',
        params: {
          ...(p?.status ? { status: p.status } : {}),
          ...(p?.project_id ? { project_id: p.project_id } : {}),
        },
      }),
      providesTags: ['LogistOrder'],
    }),

    /** Карточка заказа. */
    getLogistOrder: b.query<{ order: LogistOrder }, string>({
      query: (id) => `/agents/logist/orders/${id}`,
      providesTags: ['LogistOrder'],
    }),

    /** Создать заказ (ручной ввод). */
    createLogistOrder: b.mutation<{ order: LogistOrder }, Record<string, unknown>>({
      query: (body) => ({ url: '/agents/logist/orders', method: 'POST', body }),
      invalidatesTags: ['LogistOrder'],
    }),

    /** Обновить заказ. */
    updateLogistOrder: b.mutation<
      { order: LogistOrder },
      { id: string; body: Record<string, unknown> }
    >({
      query: ({ id, body }) => ({ url: `/agents/logist/orders/${id}`, method: 'PUT', body }),
      invalidatesTags: ['LogistOrder'],
    }),

    /** Сменить статус заказа (§7.4). */
    setLogistOrderStatus: b.mutation<{ order: LogistOrder }, { id: string; status: string }>({
      query: ({ id, status }) => ({
        url: `/agents/logist/orders/${id}/status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: ['LogistOrder'],
    }),

    /** Удалить черновик заказа (статус new/incomplete). */
    deleteLogistOrder: b.mutation<{ deleted: string }, string>({
      query: (id) => ({ url: `/agents/logist/orders/${id}`, method: 'DELETE' }),
      invalidatesTags: ['LogistOrder'],
    }),

    /** Извлечь ЧЕРНОВИК заказа из свободного текста письма (LLM, Волна 2).
     *  Ничего не сохраняет — поля для пред-заполнения формы (HITL). */
    extractLogistOrder: b.mutation<LogistOrderExtract, { text: string }>({
      query: (body) => ({ url: '/agents/logist/orders/extract', method: 'POST', body }),
    }),

    // ---- Серии рейсов (§7.2) ---------------------------------------------

    /** Серии заказа. */
    listLogistSeries: b.query<{ series: LogistTripSeries[] }, { order_id: string }>({
      query: ({ order_id }) => ({ url: '/agents/logist/series', params: { order_id } }),
      providesTags: ['LogistSeries'],
    }),

    /** Создать серию-шаблон. */
    createLogistSeries: b.mutation<{ series: LogistTripSeries }, Record<string, unknown>>({
      query: (body) => ({ url: '/agents/logist/series', method: 'POST', body }),
      invalidatesTags: ['LogistSeries'],
    }),

    /** Сгенерировать N рейсов из серии. */
    generateLogistSeries: b.mutation<{ generated: number; trips: LogistTrip[] }, string>({
      query: (id) => ({ url: `/agents/logist/series/${id}/generate`, method: 'POST' }),
      invalidatesTags: ['LogistSeries', 'LogistTrip'],
    }),

    /** Удалить серию. */
    deleteLogistSeries: b.mutation<{ deleted: string }, string>({
      query: (id) => ({ url: `/agents/logist/series/${id}`, method: 'DELETE' }),
      invalidatesTags: ['LogistSeries'],
    }),

    // ---- Рейсы (ходки) ---------------------------------------------------

    /** Список рейсов (фильтры order_id / series_id / status). */
    listLogistTrips: b.query<
      { trips: LogistTrip[] },
      { order_id?: string; series_id?: string; status?: string }
    >({
      query: (params) => ({ url: '/agents/logist/trips', params }),
      providesTags: ['LogistTrip'],
    }),

    /** Создать одиночный рейс. */
    createLogistTrip: b.mutation<{ trip: LogistTrip }, Record<string, unknown>>({
      query: (body) => ({ url: '/agents/logist/trips', method: 'POST', body }),
      invalidatesTags: ['LogistTrip'],
    }),

    /** Назначить/заменить водителя, ТС и прицеп. */
    assignLogistTrip: b.mutation<
      { trip: LogistTrip },
      { id: string; body: Record<string, unknown> }
    >({
      query: ({ id, body }) => ({ url: `/agents/logist/trips/${id}/assign`, method: 'POST', body }),
      invalidatesTags: ['LogistTrip'],
    }),

    /** Сменить статус рейса (§7.4). */
    setLogistTripStatus: b.mutation<{ trip: LogistTrip }, { id: string; status: string }>({
      query: ({ id, status }) => ({
        url: `/agents/logist/trips/${id}/status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: ['LogistTrip'],
    }),

    /**
     * Записать сведения о погрузке, обязательные по формату ЭТрН: время работы
     * машины на площадке и характеристики груза. Пустые поля не стирают ранее
     * записанное — форму заполняют по мере того, как факты становятся известны.
     */
    setLogistTripLoadingDetails: b.mutation<
      { trip: LogistTrip },
      { id: string } & LogistTripLoadingDetails
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/logist/trips/${id}/loading-details`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['LogistTrip'],
    }),

    /**
     * Контакты и адрес организации для накладной. Оператор их не отдаёт —
     * заполняются один раз на контрагента и идут во все его документы.
     */
    setLogistEdoBoxRequisites: b.mutation<
      LogistEdoBox,
      {
        id: string;
        contact_phone: string | null;
        contact_email: string | null;
        address: LogistPostalAddress | null;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/logist/edo-boxes/${id}/requisites`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['LogistEdoBox'],
    }),

    /** Виды тары для накладной — справочник с сервера. */
    listLogistContainerTypes: b.query<{ items: LogistContainerType[] }, void>({
      query: () => '/agents/logist/trips/container-types',
    }),

    /** Удалить рейс. */
    deleteLogistTrip: b.mutation<{ deleted: string }, string>({
      query: (id) => ({ url: `/agents/logist/trips/${id}`, method: 'DELETE' }),
      invalidatesTags: ['LogistTrip'],
    }),

    // ---- Весовой контроль (§8.4) -----------------------------------------

    /** Записи взвешивания рейса. */
    listLogistWeighings: b.query<{ weighings: LogistTripWeighing[] }, string>({
      query: (tripId) => `/agents/logist/trips/${tripId}/weighings`,
      providesTags: ['LogistWeighing'],
    }),

    /** Добавить взвешивание (нетто либо брутто+тара). Пересчитывает вердикт. */
    addLogistWeighing: b.mutation<
      { weighing: LogistTripWeighing; trip: LogistTrip },
      { tripId: string; body: Record<string, unknown> }
    >({
      query: ({ tripId, body }) => ({
        url: `/agents/logist/trips/${tripId}/weighings`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['LogistWeighing', 'LogistTrip'],
    }),

    /** Удалить запись взвешивания. Пересчитывает вердикт. */
    deleteLogistWeighing: b.mutation<
      { trip: LogistTrip },
      { tripId: string; weighingId: string }
    >({
      query: ({ tripId, weighingId }) => ({
        url: `/agents/logist/trips/${tripId}/weighings/${weighingId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['LogistWeighing', 'LogistTrip'],
    }),

    /** Задать/снять явный лимит массы (спецразрешение/override). */
    setLogistTripPermit: b.mutation<
      { trip: LogistTrip },
      { id: string; max_permitted_mass_kg: number | null }
    >({
      query: ({ id, max_permitted_mass_kg }) => ({
        url: `/agents/logist/trips/${id}/permit`,
        method: 'POST',
        body: { max_permitted_mass_kg },
      }),
      invalidatesTags: ['LogistTrip'],
    }),

    /** Пересчитать вердикт весового контроля по требованию. */
    recheckLogistTrip: b.mutation<{ trip: LogistTrip }, string>({
      query: (id) => ({ url: `/agents/logist/trips/${id}/recheck`, method: 'POST' }),
      invalidatesTags: ['LogistTrip'],
    }),

    // ---- Реестр ЭТрН и обмен с оператором (§7.4, §1.3) -------------------

    /** Реестр ЭТрН (фильтры trip_id / state / operator_status). `enabled` —
     *  включён ли контур обмена (OSNOVA_LOGIST_ENABLED). */
    listLogistRegistry: b.query<
      { etrn: LogistEtrn[]; enabled: boolean },
      { trip_id?: string; state?: string; operator_status?: string } | void
    >({
      query: (p) => ({ url: '/agents/logist/registry', params: p ?? undefined }),
      providesTags: ['LogistEtrn'],
    }),

    /** Карточка ЭТрН. */
    getLogistEtrn: b.query<{ etrn: LogistEtrn }, string>({
      query: (id) => `/agents/logist/etrn/${id}`,
      providesTags: ['LogistEtrn'],
    }),

    /** Сформировать и отправить ЭТрН по рейсу (идемпотентно). За флагом. */
    submitLogistEtrn: b.mutation<{ etrn: LogistEtrn; resubmitted: boolean }, string>({
      query: (tripId) => ({ url: `/agents/logist/trips/${tripId}/etrn`, method: 'POST' }),
      invalidatesTags: ['LogistEtrn'],
    }),

    /** Опросить юр-статус ЭТрН у оператора. За флагом. */
    refreshLogistEtrn: b.mutation<{ etrn: LogistEtrn }, string>({
      query: (id) => ({ url: `/agents/logist/etrn/${id}/refresh`, method: 'POST' }),
      invalidatesTags: ['LogistEtrn'],
    }),

    /** Аннулировать ЭТрН у оператора. За флагом. */
    revokeLogistEtrn: b.mutation<{ etrn: LogistEtrn }, { id: string; reason?: string }>({
      query: ({ id, reason }) => ({
        url: `/agents/logist/etrn/${id}/revoke`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['LogistEtrn'],
    }),

    /** Объяснить ошибку оператора простым языком (LLM, Волна 2). Ничего не
     *  меняет; `explanation` = null, если ошибки нет либо LLM недоступен. */
    explainLogistEtrn: b.mutation<{ explanation: string | null }, string>({
      query: (id) => ({ url: `/agents/logist/etrn/${id}/explain`, method: 'POST' }),
    }),

    // ---- Входящие ЭТрН (Волна 2) -----------------------------------------

    /** Список входящих ЭТрН (фильтр ?status). */
    listLogistInbox: b.query<
      { inbox: LogistEtrnInbox[]; enabled: boolean },
      { status?: string } | void
    >({
      query: (p) => ({ url: '/agents/logist/etrn/inbox', params: p ?? undefined }),
      providesTags: ['LogistInbox'],
    }),

    /** Пометить входящий ЭТрН пропущенным (не наш / дубль). */
    skipLogistInbox: b.mutation<{ inbox: LogistEtrnInbox }, string>({
      query: (id) => ({ url: `/agents/logist/etrn/inbox/${id}/skip`, method: 'POST' }),
      invalidatesTags: ['LogistInbox'],
    }),

    /** Привязать входящий ЭТрН к поставке МТО / приёмке Прораба. */
    linkLogistInbox: b.mutation<
      { inbox: LogistEtrnInbox },
      { id: string; linked_kind: string; linked_id: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/logist/etrn/inbox/${id}/link`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['LogistInbox'],
    }),

    /** Детерминированный авто-подбор поставки/приёмки. Единственное сильное
     *  совпадение — авто-связка (linked=true); иначе кандидаты на HITL. */
    automatchLogistInbox: b.mutation<
      { linked: boolean; inbox?: LogistEtrnInbox; candidates: LogistMatchCandidate[] },
      string
    >({
      query: (id) => ({ url: `/agents/logist/etrn/inbox/${id}/automatch`, method: 'POST' }),
      invalidatesTags: ['LogistInbox'],
    }),
    /** Список ящиков ЭДО контрагентов (?query — поиск по ИНН/наименованию). */
    listLogistEdoBoxes: b.query<
      { operator: string; items: LogistEdoBox[] },
      { query?: string; active?: boolean } | void
    >({
      query: (args) => ({
        url: '/agents/logist/edo-boxes',
        params: args ?? undefined,
      }),
      providesTags: ['LogistEdoBox'],
    }),

    /** Ручной ввод ящика — резервный путь, когда оператор недоступен. */
    createLogistEdoBox: b.mutation<LogistEdoBox, Record<string, unknown>>({
      query: (body) => ({ url: '/agents/logist/edo-boxes', method: 'POST', body }),
      invalidatesTags: ['LogistEdoBox'],
    }),

    /**
     * Спросить реквизиты у оператора по ИНН и сохранить. Основной путь
     * наполнения: оператор знает и ящик, и зарегистрирован ли контрагент в
     * Логистике — угадать это на нашей стороне нельзя. Одному ИНН может
     * отвечать несколько филиалов, поэтому список.
     */
    lookupLogistEdoBox: b.mutation<
      { operator: string; saved: boolean; items?: LogistEdoBox[]; found?: unknown[] },
      { inn: string; kpp?: string; save?: boolean }
    >({
      query: (body) => ({ url: '/agents/logist/edo-boxes/lookup', method: 'POST', body }),
      invalidatesTags: ['LogistEdoBox'],
    }),

    /** Деактивация вместо удаления: на ящик ссылаются выпущенные документы. */
    deactivateLogistEdoBox: b.mutation<LogistEdoBox, string>({
      query: (id) => ({ url: `/agents/logist/edo-boxes/${id}`, method: 'DELETE' }),
      invalidatesTags: ['LogistEdoBox'],
    }),

    activateLogistEdoBox: b.mutation<LogistEdoBox, string>({
      query: (id) => ({ url: `/agents/logist/edo-boxes/${id}/activate`, method: 'POST' }),
      invalidatesTags: ['LogistEdoBox'],
    }),

    /**
     * Цепочка титулов документа — части ЭТрН, подписанные сторонами.
     * Заполняется сверкой статусов; выпуск титулов сверх Т1/Т3 у оператора
     * идёт через его кабинет, у API метода для них нет.
     */
    listLogistEtrnTitles: b.query<{ titles: LogistEtrnTitle[] }, string>({
      query: (id) => ({ url: `/agents/logist/etrn/${id}/titles` }),
      providesTags: ['LogistEtrn'],
    }),

    /**
     * Оформить приёмку груза по входящему документу — титул грузополучателя.
     * Как и выпуск накладной, доводится до черновика: подпись ставит человек
     * в кабинете оператора.
     */
    acceptLogistDelivery: b.mutation<
      {
        external_id: string;
        operator_status: string | null;
        /** true — груз уже принимали: повторный документ не выпускался. */
        already_submitted?: boolean;
      },
      { id: string; acceptance: LogistDeliveryAcceptance }
    >({
      query: ({ id, acceptance }) => ({
        url: `/agents/logist/etrn/inbox/${id}/delivery`,
        method: 'POST',
        body: acceptance,
      }),
      invalidatesTags: ['LogistInbox', 'LogistEtrn'],
    }),

    /** Карточка доказательного архива (без файла). */
    getLogistEvidence: b.query<{ evidence: LogistEtrnEvidence | null }, string>({
      query: (id) => ({ url: `/agents/logist/etrn/${id}/evidence` }),
      providesTags: ['LogistEvidence'],
    }),

    /**
     * Забрать у оператора комплект документов и сохранить в нашем контуре.
     * Делается один раз при закрытии перевозки — фоновый обмен выполняет это
     * сам, кнопка нужна для ручной пересборки и для старых документов.
     */
    archiveLogistEvidence: b.mutation<{ evidence: LogistEtrnEvidence }, string>({
      query: (id) => ({ url: `/agents/logist/etrn/${id}/evidence`, method: 'POST' }),
      invalidatesTags: ['LogistEvidence'],
    }),

    /** Лента задач: что требует внимания прямо сейчас. */
    listLogistTasks: b.query<
      { tasks: LogistTask[]; open: number; critical: number },
      { state?: string; kind?: string } | void
    >({
      query: (args) => ({ url: '/agents/logist/tasks', params: args ?? undefined }),
      providesTags: ['LogistTask'],
    }),

    /**
     * «Вижу, занимаюсь». Документов не меняет: если причина не устранена,
     * задача останется в ленте.
     */
    ackLogistTask: b.mutation<{ task: LogistTask }, string>({
      query: (id) => ({ url: `/agents/logist/tasks/${id}/ack`, method: 'POST' }),
      invalidatesTags: ['LogistTask'],
    }),

  }),
});

export const {
  useListLogistTasksQuery,
  useAckLogistTaskMutation,
  useGetLogistEvidenceQuery,
  useArchiveLogistEvidenceMutation,
  useAcceptLogistDeliveryMutation,
  useListLogistEtrnTitlesQuery,
  useListLogistEdoBoxesQuery,
  useCreateLogistEdoBoxMutation,
  useLookupLogistEdoBoxMutation,
  useDeactivateLogistEdoBoxMutation,
  useActivateLogistEdoBoxMutation,
  useListLogistDictQuery,
  useCreateLogistDictMutation,
  useUpdateLogistDictMutation,
  useActivateLogistDictMutation,
  useDeactivateLogistDictMutation,
  useListLogistOrdersQuery,
  useGetLogistOrderQuery,
  useCreateLogistOrderMutation,
  useUpdateLogistOrderMutation,
  useSetLogistOrderStatusMutation,
  useDeleteLogistOrderMutation,
  useExtractLogistOrderMutation,
  useListLogistSeriesQuery,
  useCreateLogistSeriesMutation,
  useGenerateLogistSeriesMutation,
  useDeleteLogistSeriesMutation,
  useListLogistTripsQuery,
  useCreateLogistTripMutation,
  useAssignLogistTripMutation,
  useSetLogistTripStatusMutation,
  useSetLogistTripLoadingDetailsMutation,
  useListLogistContainerTypesQuery,
  useSetLogistEdoBoxRequisitesMutation,
  useDeleteLogistTripMutation,
  useListLogistWeighingsQuery,
  useAddLogistWeighingMutation,
  useDeleteLogistWeighingMutation,
  useSetLogistTripPermitMutation,
  useRecheckLogistTripMutation,
  useListLogistRegistryQuery,
  useGetLogistEtrnQuery,
  useSubmitLogistEtrnMutation,
  useRefreshLogistEtrnMutation,
  useRevokeLogistEtrnMutation,
  useExplainLogistEtrnMutation,
  useListLogistInboxQuery,
  useSkipLogistInboxMutation,
  useLinkLogistInboxMutation,
  useAutomatchLogistInboxMutation,
  useListLogistSignersQuery,
  useListLogistSignerStatusesQuery,
  useUpsertLogistSignerMutation,
  useSetLogistSignerActiveMutation,
} = logistApi;
